import { useState, useEffect } from "react";
import API from "@/lib/api";
import { Play, Search, Loader2, Youtube, Star } from "lucide-react";

/**
 * YouTube video library card — drops into PatientDashboard or
 * PatientProfile. Pulls the curated catalog (super-admin can feature/
 * hide) and lets the patient search by title/description. Plays inline
 * via an embedded iframe so we never leave the GLPFlo experience for
 * watching short how-to clips.
 *
 * If the admin hasn't connected the YouTube API key yet the list is
 * empty and we render a friendly "Coming soon" placeholder with a
 * direct link to the channel.
 */
export default function YouTubeLibraryCard() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [channelUrl, setChannelUrl] = useState("https://www.youtube.com/channel/UC2NOTygbPH-3JCkElwzNEWw");
  const [activeId, setActiveId] = useState(null);

  // Fetch channel info once + initial video list.
  useEffect(() => {
    API.get("/youtube/channel").then(({ data }) => {
      if (data?.channel_url) setChannelUrl(data.channel_url);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    // Debounce search by 250ms so each keystroke doesn't fire a request.
    const t = setTimeout(() => {
      API.get(`/youtube/videos${query ? `?search=${encodeURIComponent(query)}` : ""}`)
        .then(({ data }) => { if (!cancelled) setVideos(Array.isArray(data) ? data : []); })
        .catch(() => { if (!cancelled) setVideos([]); })
        .finally(() => { if (!cancelled) setLoading(false); });
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [query]);

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6" data-testid="youtube-library-card">
      <div className="flex items-center justify-between mb-4 gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center shrink-0">
            <Youtube className="w-5 h-5 text-red-600" />
          </div>
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
              GLPFlo Video Library
            </h3>
            <p className="text-xs text-slate-500 truncate">How-to guides, medication info, and patient education</p>
          </div>
        </div>
        <a
          href={channelUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="hidden sm:inline-flex items-center gap-1.5 text-xs font-medium text-red-600 hover:text-red-700 shrink-0"
          data-testid="youtube-channel-link"
        >
          Open channel <Play className="w-3 h-3" />
        </a>
      </div>

      <div className="relative mb-4">
        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search videos by title or topic…"
          className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-purple-400"
          data-testid="youtube-search-input"
        />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-10 text-slate-400">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
        </div>
      ) : videos.length === 0 ? (
        <div className="text-center py-10 text-slate-500" data-testid="youtube-library-empty">
          <Youtube className="w-10 h-10 mx-auto mb-3 text-slate-300" />
          <p className="text-sm font-medium text-slate-700 mb-1">
            {query ? "No videos matched your search." : "New videos coming soon!"}
          </p>
          <p className="text-xs text-slate-500 mb-3">
            {query ? "Try a different keyword." : "We're publishing patient how-to guides to our YouTube channel — check back here or visit the channel directly."}
          </p>
          <a
            href={channelUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-600 text-white text-xs font-medium hover:bg-red-700 transition"
          >
            <Youtube className="w-3.5 h-3.5" /> Visit GLPFlo on YouTube
          </a>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3">
          {videos.map((v) => (
            <div
              key={v.video_id}
              className="rounded-lg border border-slate-200 overflow-hidden hover:border-purple-300 hover:shadow-sm transition group"
              data-testid={`youtube-video-${v.video_id}`}
            >
              {activeId === v.video_id ? (
                <div className="aspect-video bg-black">
                  <iframe
                    title={v.title}
                    src={`${v.embed_url}?autoplay=1&rel=0`}
                    className="w-full h-full"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setActiveId(v.video_id)}
                  className="block w-full text-left relative aspect-video bg-slate-100"
                  data-testid={`youtube-play-${v.video_id}`}
                >
                  {v.thumbnail_url && (
                    <img
                      src={v.thumbnail_url}
                      alt={v.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  )}
                  <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition">
                    <div className="w-12 h-12 rounded-full bg-white/90 group-hover:bg-white flex items-center justify-center shadow">
                      <Play className="w-5 h-5 text-red-600 ml-0.5" fill="currentColor" />
                    </div>
                  </div>
                  {v.featured && (
                    <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-amber-500 text-white text-[10px] font-semibold uppercase tracking-wide flex items-center gap-1">
                      <Star className="w-3 h-3" fill="currentColor" /> Featured
                    </div>
                  )}
                </button>
              )}
              <div className="p-3">
                <h4 className="text-sm font-semibold text-slate-800 line-clamp-2 leading-snug" title={v.title}>
                  {v.title}
                </h4>
                {v.published_at && (
                  <p className="text-[11px] text-slate-500 mt-1">
                    {new Date(v.published_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
